import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {useTheme} from "../../contexts/theme";
import {green} from "@mui/material/colors";
import {Button, Container, duration, Skeleton, TableFooter, TableSortLabel, TextField, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {formatDuration, formatProductionId, formatTime} from "../../utils/utils";
import {useEffect, useMemo, useState} from "react";
import TextinputField from "../CommonComponents/TextInputField/TextinputField";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {formatUTCDate} from "../../utils/dateTimeutils";

type rowsData = {
    date: string,
    competition: string,
    matchName: string,
    matchDate: string,
    matchLang: string,
    status: string,
    duration: string,
    clips: number,
    amount: number
}


type Order = 'asc' | 'desc'


interface DashboardContentProps {
    rowsData: rowsData[];
    setrowsData: React.Dispatch<React.SetStateAction<rowsData[]>>;
    loading: boolean;
}


export default function DashboardTable({rowsData, setrowsData, loading}: DashboardContentProps) {
    const [myRowsData, setMyRowsData] = useState([...rowsData]);
    useEffect(() => {
        // Crea una copia per valore dell'array `data`
        setMyRowsData([...rowsData]);  // Utilizza l'operatore spread per clonare l'array

    }, [rowsData]);

    const theme = useTheme();

    // Stato per la colonna attiva per il sorting e la direzione (asc/desc)
    const [orderBy, setOrderBy] = useState<keyof typeof myRowsData[0]>("date"); // Ordina inizialmente per "date"
    const [order, setOrder] = useState<"asc" | "desc">("asc");


    function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    type Order = 'asc' | 'desc';

    function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key,
    ): (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string },
    ) => number {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    // Funzione per gestire il clic sull'intestazione di una colonna
    const handleSort = (property: keyof typeof myRowsData[0]) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
        setMyRowsData([...rowsData].sort(getComparator(order, orderBy)));
    };

    function durationTotal(): string {
        // Accumulate total seconds from all rows
        let totalSeconds = 0;

        myRowsData.forEach((row) => {
            // Split the duration string into hours, minutes, seconds
            if (row['duration'] === "-") {
                return;
            }
            const [hh, mm, ss] = row['duration'].split(':').map(Number);

            // Convert all to seconds
            totalSeconds += hh * 3600 + mm * 60 + ss;
        });

        // Convert totalSeconds back into HH:MM:SS
        const hours = Math.floor(totalSeconds / 3600);
        const remainder = totalSeconds % 3600;
        const minutes = Math.floor(remainder / 60);
        const seconds = remainder % 60;

        // Pad with zeros to always have two digits
        const hhStr = hours.toString().padStart(2, '0');
        const mmStr = minutes.toString().padStart(2, '0');
        const ssStr = seconds.toString().padStart(2, '0');

        return `${hhStr}:${mmStr}:${ssStr}`;
    }


    function total(field: string): number {
        let sum = 0;
        myRowsData.forEach((row, i) => {
            if (field === 'clips')
                sum += row['clips'];
            else if (field === 'amount')
                sum += row['amount'];
        })
        return sum;
    }

    const filterData = (event: React.ChangeEvent<HTMLInputElement>) => {
        let inputText = event.target.value.trim();

        if (inputText.length === 0) {
            // Se vuoto, ripristina tutte le righe
            setMyRowsData(rowsData); // Assicurati di avere una copia delle righe originali
        } else {
            setMyRowsData([]);
            const temprowsData = myRowsData.filter((row) => {
                let booleanAcc = false;
                for (const key in row) {
                    booleanAcc = booleanAcc || (((row as any)[key]).toString().toLowerCase().includes(inputText.toLowerCase()));
                }
                return booleanAcc
            });
            setMyRowsData(temprowsData)
        }
    };

    function jsonToCsv(jsonArray: any[]): string {
        if (jsonArray.length === 0) {
            return '';
        }
        const headers = Object.keys(jsonArray[0]);
        const csvrowsData = [];
        csvrowsData.push(headers.join(','));
        for (const row of jsonArray) {
            const values = headers.map(header => JSON.stringify(row[header], (key, value) => value || '')).join(',');
            csvrowsData.push(values);
        }

        return csvrowsData.join('\n');
    }

    function downloadCSV() {

        const csv = jsonToCsv(myRowsData);
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'InBrief_billingData.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    function Animations() {

        const rowsSkeleton = [];

        for (let i = 0; i < 10; i++) {
            rowsSkeleton.push(<TableRow>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           component="th" scope="row">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>

                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
                <TableCell sx={{backgroundColor: theme.palette.blue, color: theme.palette.white, borderBottom: "none"}}
                           align="left">
                    <Skeleton animation="pulse"/>
                </TableCell>
            </TableRow>)
        }

        return (
            <TableBody>
                {rowsSkeleton}
            </TableBody>
        );
    }

    return (
        <Box className="box-container" sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden"
        }}>
            <Grid container sx={{marginBottom: theme.spacing.md}}>
                <Grid item>
                    <TransparentButton text={'Export CSV'} onClick={downloadCSV}/>
                </Grid>
                <Grid item xs={2} md={3} lg={3}>
                    <TextinputField name={'filter'}
                                    id='outlined-basic'
                                    placeholder={'Filter'}
                                    type={'search'}
                                    onChange={filterData}
                                    borderColor={theme.palette.white}
                                    marginLeft={theme.spacing.md}
                                    borderBottomColor='transparent'
                                    variant={'outlined'}
                                    borderRadius={theme.border.radius.md}
                                    color={theme.palette.white}
                    />
                </Grid>
            </Grid>
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    minHeight: 0,
                    height: '100%'
                }}
            >
                <TableContainer component={Paper} sx={{height: '100%', flex: 1, minHeight: 0, overflow: "auto", backgroundColor: theme.palette.blue}}>
                    <Table stickyHeader sx={{minWidth: 650, height: '100%', flex: 1, minHeight: 0, overflow: "auto"}} aria-label="sortable table">
                        <TableHead>
                            <TableRow>
                                {/* Intestazioni ordinabili */}
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "date"}
                                        direction={orderBy === "date" ? order : "asc"}
                                        onClick={() => handleSort("date")}
                                        sx={{
                                            color: orderBy === 'date' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "competition"}
                                        direction={orderBy === "competition" ? order : "asc"}
                                        onClick={() => handleSort("competition")}
                                        sx={{
                                            color: orderBy === 'competition' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Competition
                                    </TableSortLabel>
                                </TableCell>
                                {/* Aggiungi lo stesso approccio per le altre colonne */}
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "matchName"}
                                        direction={orderBy === "matchName" ? order : "asc"}
                                        onClick={() => handleSort("matchName")}
                                        sx={{
                                            color: orderBy === 'matchName' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Match Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "matchDate"}
                                        direction={orderBy === "matchDate" ? order : "asc"}
                                        onClick={() => handleSort("matchDate")}
                                        sx={{
                                            color: orderBy === 'matchDate' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Match Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "matchLang"}
                                        direction={orderBy === "matchLang" ? order : "asc"}
                                        onClick={() => handleSort("matchLang")}
                                        sx={{
                                            color: orderBy === 'matchLang' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Match Lang
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "status"}
                                        direction={orderBy === "status" ? order : "asc"}
                                        onClick={() => handleSort("status")}
                                        sx={{
                                            color: orderBy === 'status' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "duration"}
                                        direction={orderBy === "duration" ? order : "asc"}
                                        onClick={() => handleSort("duration")}
                                        sx={{
                                            color: orderBy === 'duration' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Duration
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "clips"}
                                        direction={orderBy === "clips" ? order : "asc"}
                                        onClick={() => handleSort("clips")}
                                        sx={{
                                            color: orderBy === 'clips' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Clips
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: 'none'
                                }}>
                                    <TableSortLabel
                                        active={orderBy === "amount"}
                                        direction={orderBy === "amount" ? order : "asc"}
                                        onClick={() => handleSort("amount")}
                                        sx={{
                                            color: orderBy === 'amount' ? theme.palette.orange : theme.palette.white,
                                            "&.Mui-active": {
                                                color: theme.palette.orange, // Colore quando è attivo
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                            '&:hover': {
                                                color: theme.palette.orange, // Colore quando è in hover
                                                "& .MuiTableSortLabel-icon": {
                                                    color: theme.palette.orange
                                                }
                                            },
                                        }} // Colore dell'intestazione
                                    >
                                        Amount
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {!loading ? (
                            <TableBody sx={{
                                flex: 1,
                                overflow: "auto",
                                minHeight: 0,
                                height: '100%'
                            }}>
                                {
                                    myRowsData.map((row, index) => (
                                        <TableRow key={`${row.matchName}-${index}`}>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} component="th" scope="row">
                                                {formatUTCDate(row.date)}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.competition}
                                            </TableCell>
                                            {/* Le altre celle */}
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.matchName}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {formatUTCDate(row.matchDate)}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.matchLang}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.status}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.duration}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.clips}
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: theme.palette.blue,
                                                color: theme.palette.white,
                                                borderBottom: "none"
                                            }} align="left">
                                                {row.amount} USD
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        ) : Animations()}
                        <TableFooter sx={{
                            flex: 1,
                            overflow: "auto",
                            minHeight: 0,
                            height: '100%'
                        }}>
                            <TableRow
                                sx={{
                                    position: "sticky",
                                    bottom: 0,
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                }}
                            >
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row">
                                    <Typography variant='caption'
                                                sx={{fontWeight: theme.fontWeights.value}}>Total</Typography>
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row"></TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row"></TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row"></TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row"></TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row"></TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row">
                                    {durationTotal()}
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row">
                                    {total('clips')}
                                </TableCell>
                                <TableCell sx={{
                                    backgroundColor: theme.palette.tableHeaderBlue,
                                    color: theme.palette.white,
                                    borderBottom: "none"
                                }} component="th" scope="row">
                                    <Typography variant='caption'
                                                sx={{fontWeight: theme.fontWeights.value}}>SubTotal:</Typography>{total('amount')} USD <br/>
                                    <Typography variant='caption'
                                                sx={{fontWeight: theme.fontWeights.value}}>VAT(22%):</Typography>{total('amount') * (0.22)} USD <br/>
                                    <Typography variant='caption'
                                                sx={{fontWeight: theme.fontWeights.value}}>Total:</Typography>{total('amount') + total('amount') * (0.22)} USD
                                </TableCell>
                            </TableRow>

                        </TableFooter>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}